import { action, makeAutoObservable, runInAction } from "mobx";
import { AxiosError, AxiosInstance } from "axios";
import { AffiliateItem } from "@/types/Settings";
import {
  AdminItem,
  BR_STATUS,
  EnterpriseAccount,
  EnterpriseAccountStats,
  EnterpriseAccountStatus,
  failure,
  isOk,
  ok,
  pending,
  Result,
  Whitelabel,
} from "@/common/common";
import { logDebug } from "@/utils/log";
import { ValidationErrorWithContext } from "@/types/excelImportDomain";
import { identity, isEmpty, join } from "lodash";
import { getAllCountriesSortedByName } from "@/utils/countries";
import { ListProductResponse, ProductSubscriptionResponse } from "@/payment/Payment";
import { newUuid } from "@/utils/uuid";
import { Language } from "@/i18n/languages";
import { TMessage } from "@/i18n/tt";
import { capitalize, isNotBlank } from "@/utils/strings";
import { History } from "history";
import { TMessageKey } from "@/i18n/types.gen";
import { TagColor } from "@/components-v2/Tag";
import { DecisionType } from "@/common/decisions";
import {
  createEmailField,
  LocalDateFieldModel,
  LocalizedStringFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import { ProductDescription } from "@/common/product";
import { AnnouncementDTO, GetAnnouncementResponse } from "@/announcement/announcementDomain";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { superadminUrl } from "@/utils/navigation";
import { DestinationFolder, DestinationFoldersResponse } from "@/archive/archiveDomain";
import { defaultZipImportFolder, ZipImportResult, zipImportErrorMessage } from "./zipImportDomain";
import { downloadFile, getFilenameFromHeaders, isBadRequestError } from "@/api";
import {
  ImpersonationReason,
  impersonationReasonLabel,
  impersonationReasonValues,
} from "@/types/UserInfo";

export type Invitation = {
  invitationId: string;
  invitedBy: string;
  isUsed: boolean;
  usedBy: string;
  created: string;
  expires: string;
};

export type DialogportenSystemUser = {
  id: string;
  externalRef: string;
  userType: string;
  created: string;
  isDeleted: boolean;
};

export type DialogportenStatement = {
  dialogId: string;
  serviceResource: string;
  party: string;
  org: string;
  status: string;
  extendedStatus?: string;
  externalReference?: string;
  title?: string;
  summary?: string;
  createdAt: string;
  updatedAt: string;
  dueAt?: string;
  attachmentCount?: number;
  reportingYear?: number;
};

export type DialogportenStatementsResponse = {
  systemUser?: DialogportenSystemUser;
  agentSystemUser?: DialogportenSystemUser;
  statements: DialogportenStatement[];
};

export type DialogportenAttachmentUrl = {
  mediaType: string;
  consumerType?: "Gui" | "Api";
  url: string;
  isAuthorized: boolean;
};

export type DialogportenAttachment = {
  attachmentId: string;
  filename: string;
  expiresAt?: string;
  urls: DialogportenAttachmentUrl[];
};

export type DialogportenTransmission = {
  transmissionId: string;
  type: string;
  createdAt: string;
  relatedTransmissionId?: string;
  title?: string;
  summary?: string;
  isAuthorized?: boolean;
  attachments: DialogportenAttachment[];
};

export type DialogportenDialog = {
  dialogId: string;
  transmissions: DialogportenTransmission[];
};

export type PrefillSubForm = {
  name: string;
  identifier?: string;
  numberOfShares: number;
};

export type PrefillShareClass = {
  totalShareCapital?: number;
  shareCapitalForShareType?: number;
  nominalValue?: number;
  numberOfShares?: number;
  totalPaidInShareCapitalForShareType?: number;
  totalSharePremiumForShareType?: number;
  subForms: PrefillSubForm[];
};

export type PrefillDocument = {
  aksjeklasse: string;
  dokument: string;
};

export type PrefillResponse = {
  oppgaveStatus: string;
  fetchedAt: string;
  shareClasses: Record<string, PrefillShareClass>;
  documents: PrefillDocument[];
};

type GenerateAdminLinkResponse = {
  link: string;
  expiry: string;
  isCustomer: boolean;
};

type GrantAdminAccessResponse = {
  addedOrgNos: string[];
  failedOrgNos: string[];
};

export type PendingDecisionListItem = {
  id: string;
  projectId: string;
  orgNo: string;
  companyName: string;
  status: BR_STATUS;
  sentToBRTime: string;
  signerReminderDate: string;
  updateTime: string;
  decisionType: DecisionType;
  responsibleId?: string;
  isRegistration: boolean;
  submissionReference?: number;
};

export const BR_STATUS_LABEL: { [key in BR_STATUS]: { color: TagColor; label: TMessageKey } } = {
  SENT_TO_BR: { color: "warning", label: "new" },
  DELETED: { color: "error", label: "general.deleted" },
  SENT_FOR_SIGNING: { color: "neutral", label: "sent-for-signing" },
  PROCESSING: { color: "info", label: "processing" },
  BR_APPROVED: { color: "success", label: "approved" },
  REJECTED: { color: "error", label: "rejected" },
  NEEDS_DOCUMENTATION: { color: "warning", label: "needs-documentation" },
} as const;

export type CompanySearchResponse = {
  orgNo: string;
  companyName: string;
  isCustomer: boolean;
  hasAcceptedCompanyTerms: boolean;
  adminUsers: AdminItem[];
  invitations: Invitation[];
  subscription: ProductSubscriptionResponse | undefined;
  affiliates: AffiliateItem[];
  isDemo: boolean;
  hasSentShareholderRegisterStatement: boolean;
  altinnYear: number;
  isDeleted: boolean;
  deletedBy?: string;
  deletedAt?: string;
  ownerDatabase?: string;
  enterpriseAccountId?: string;
  enterpriseAccounts: EnterpriseAccount[];
};

export type AdminPageState = "initial" | "isOnboarding" | "onboardingSuccess";

export type CustomerListing = {
  orgNo: string;
  name: string;
  subscriptionName?: string | null;
};

export const CUSTOMER_FILTER_NO_SUBSCRIPTION = "__none__";

export class CreateSubscriptionForm {
  subscriptionType = "manual";
  productId?: string;
  priceId: string = "";
  expiresAt: LocalDateFieldModel;
  products: ProductDescription[] = [];

  constructor() {
    this.expiresAt = new LocalDateFieldModel();
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setSubscriptionType(subscriptionType: string) {
    this.subscriptionType = subscriptionType;
  }

  setProducts(products: ProductDescription[]) {
    this.products = products;
    this.productId = products[0].productId;
  }

  setProductId(productId: string) {
    this.productId = productId;
  }

  setPriceId(priceId: string) {
    this.priceId = priceId;
  }

  get prices() {
    const product = this.products.find((p) => p.productId === this.productId);
    return product?.prices;
  }

  validate() {
    this.expiresAt.validate();
  }

  get isValid() {
    const expiresValid = this.expiresAt.isValid;
    const productIdValid = isNotBlank(this.productId);
    return expiresValid && productIdValid;
  }
}

export class UpdateSubscriptionForm {
  subscriptionEndDate: LocalDateFieldModel = new LocalDateFieldModel();
}

export class InviteAdministratorForm {
  email = "";
  language: Language = Language.NO;
  validationErrors: string[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  setEmail(email: string) {
    this.email = email;
  }

  setLanguage(language: Language) {
    this.language = language;
  }

  validate() {
    this.validationErrors = [];
    if (this.email === "") {
      this.validationErrors.push("'email' is required");
    }
  }
}

export class AddUserForm {
  nationalId: StringFieldModel;
  country: RadioFieldModel<{ code: string; name: string }>;
  firstName: StringFieldModel;
  middleName: StringFieldModel;
  lastName: StringFieldModel;
  email: StringFieldModel;

  constructor() {
    this.firstName = new StringFieldModel({ required: true });
    this.middleName = new StringFieldModel();
    this.lastName = new StringFieldModel({ required: true });
    this.nationalId = new StringFieldModel({ required: true });
    this.email = createEmailField({ required: true });
    const countryOptions = getAllCountriesSortedByName(Language.EN);
    this.country = new RadioFieldModel<{
      code: string;
      name: string;
    }>({
      required: true,
      options: countryOptions,
      value: { code: "NO", name: "Norway" },
      idFn: (item) => item.code,
      labelFn: (item) => ({ translated: item.name }),
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  clearForm() {
    this.country.value = { code: "NO", name: "Norway" };
    this.firstName.setInputValue("");
    this.middleName.setInputValue("");
    this.lastName.setInputValue("");
    this.nationalId.setInputValue("");
    this.email.setInputValue("");
  }

  isValid(): boolean {
    this.nationalId.validate();
    this.country.validate();
    this.firstName.validate();
    this.middleName.validate();
    this.lastName.validate();
    this.email.validate();
    return (
      this.nationalId.isValid &&
      this.country.isValid &&
      this.firstName.isValid &&
      this.middleName.isValid &&
      this.lastName.isValid &&
      this.email.isValid
    );
  }
}

export class AddCompanyForm {
  organisationNumber: StringFieldModel;
  country: RadioFieldModel<{ code: string; name: string }>;
  name: StringFieldModel;

  constructor() {
    this.organisationNumber = new StringFieldModel({
      required: true,
      validator: (value) => {
        if (!/^[a-zA-Z0-9]*$/.test(value)) {
          return { key: "superadmin.add-company.orgno-invalid-characters" };
        }
        if (this.country.value?.code === "SE" && value.length !== 10) {
          return { key: "superadmin.add-company.orgno-swedish-length" };
        }
      },
    });
    const countryOptions = getAllCountriesSortedByName(Language.EN);
    this.country = new RadioFieldModel<{
      code: string;
      name: string;
    }>({
      options: countryOptions,
      value: countryOptions[0],
      idFn: (item) => item.code,
      labelFn: (item) => ({ translated: item.name }),
    });
    this.name = new StringFieldModel({ required: true });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  isValid(): boolean {
    this.organisationNumber.validate();
    this.name.validate();
    return this.organisationNumber.isValid && this.name.isValid;
  }
}

export type UserByEmailOrSocialSecurityNumber = {
  userName: string;
  name: string;
  email: string;
  userId: number;
  socialSecurityNumber: string;
  externalId: string;
  lastLogin?: string;
  lockedAt?: string;
};

export type CompanyAccess = {
  orgNo: string;
  name: string;
  isGranted: boolean;
  isFromRegister: boolean;
};

export type CustomRoleAccess = {
  orgNo: string;
  name: string;
  roleName: string;
  roleOwnerOrgNo: string;
};

export type UserAccess = {
  admin: CompanyAccess[];
  boardMember: CompanyAccess[];
  shareholder: CompanyAccess[];
  customRole: CustomRoleAccess[];
};

export type UserDetails = {
  user: UserByEmailOrSocialSecurityNumber;
  access: Result<UserAccess>;
};

export enum SearchType {
  dcompany = "superadmin.trank-search.dcompany",
  trank = "superadmin.trank-search.trank",
}

const organisationNumberValidator = (value: string): TMessage | undefined => {
  if (value.length !== 12 && value.length !== 9) {
    return { key: "superadmin.trank-search.orgnum-error" };
  }
  return undefined;
};

export type SearchUserState =
  | { type: "initial" }
  | { type: "loading" }
  | { type: "result"; users: UserByEmailOrSocialSecurityNumber[] | undefined };

export type AnnouncementState =
  | { type: "loading" }
  | { type: "empty" }
  | {
      type: "input";
      level: RadioFieldModel<AnnouncementDTO["level"]>;
      message: LocalizedStringFieldModel[];
      saving?: boolean;
    }
  | { type: "active"; announcement: AnnouncementDTO; deleting?: boolean };

export class ImpersonationDialogModel {
  reason: RadioFieldModel<ImpersonationReason>;
  details: StringFieldModel;
  isSubmitting?: boolean;

  constructor(
    readonly params: {
      onSubmit: (data: { reason: ImpersonationReason; details: string }) => void;
      onCancel: () => void;
    },
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.reason = new RadioFieldModel<ImpersonationReason>({
      value: impersonationReasonValues[0],
      idFn: identity,
      labelFn: impersonationReasonLabel,
      options: impersonationReasonValues,
      onChange: (option) => {
        this.details.setRequired(option === "OTHER");
      },
    });
    this.details = new StringFieldModel();
  }

  validate() {
    return this.details.validate();
  }

  onSubmit() {
    if (this.validate()) {
      this.isSubmitting = true;
      this.params.onSubmit({ reason: this.reason.value, details: this.details.validatedValue });
    }
  }

  onCancel() {
    this.params.onCancel();
  }
}

export class AdminAppController {
  http: AxiosInstance;
  history: History;
  messageStore: AlertMessageStore;
  adminLinkResponse: GenerateAdminLinkResponse | undefined;
  companySearchResponse: CompanySearchResponse | undefined;
  subscriptionForm: CreateSubscriptionForm;
  inviteAdministratorForm: InviteAdministratorForm;
  error: string | undefined;
  infoMessage: string | undefined;
  pageState: AdminPageState = "initial";
  deleteAutoImportTransactionResult?: Result<void, string>;
  zipImportFolders?: Result<DestinationFolder[]>;
  zipImportFolder?: DestinationFolder;
  zipImportFile?: File;
  zipImportResult?: Result<ZipImportResult, string>;
  fetchWhitelabelsResult?: Result<Whitelabel[], TMessage>;
  fetchWhitelabelLogoResult?: Result<Blob>;
  uploadWhitelabelLogoResult?: Result<string>;
  grantAdminAccessResult?: Result<GrantAdminAccessResponse>;
  addCompanyForm: AddCompanyForm;
  addUserForm: AddUserForm;
  updateSubscriptionForm: UpdateSubscriptionForm;
  affiliatesModel: RadioFieldModel<AffiliateItem> | undefined;
  searchTypeSelection: RadioFieldModel<SearchType>;
  trankSearchInput: StringFieldModel;
  isDemoCompany?: RadioFieldModel<boolean>;
  toggleDemoCompany?: Result;
  replayResult?: Result<TMessage, TMessage>;

  listProductResponse?: ListProductResponse;

  socialSecurityNumber: StringFieldModel;
  searchedUserState: SearchUserState = { type: "initial" };
  userDetailsResult?: Result<UserDetails, string>;
  unlockUserResult?: Result<void>;
  announcementState?: AnnouncementState;
  enterpriseAccountsResult?: Result<EnterpriseAccountStats[]>;
  enterpriseAccountsNameFilter: StringFieldModel = new StringFieldModel();
  enterpriseAccountsStatusFilter: RadioFieldModel<EnterpriseAccountStatus | null> =
    new RadioFieldModel({
      value: EnterpriseAccountStatus.ACTIVE,
      options: [null, EnterpriseAccountStatus.ACTIVE, EnterpriseAccountStatus.INACTIVE],
      idFn: (option) => (option ? option : "all"),
      labelFn: (option) => {
        switch (option) {
          case EnterpriseAccountStatus.ACTIVE:
            return { key: "enterprise-account.status.active" };
          case EnterpriseAccountStatus.INACTIVE:
            return { key: "enterprise-account.status.inactive" };
          default:
            return { translated: "Status: all" };
        }
      },
      iconFn: (option) => {
        switch (option) {
          case EnterpriseAccountStatus.ACTIVE:
            return { name: "circle", color: "success", weight: "fill", size: "xs" };
          case EnterpriseAccountStatus.INACTIVE:
            return { name: "circle", color: "subtle", size: "xs" };
          default:
            return "notebook";
        }
      },
    });

  impersonationDialog?: ImpersonationDialogModel;

  customerListResult?: Result<CustomerListing[]>;
  customerSearchInput: StringFieldModel = new StringFieldModel();
  customerSubscriptionFilter: string | null = null;

  dialogportenOrgNoInput: StringFieldModel = new StringFieldModel();
  dialogportenIncludeAllResources = false;
  dialogportenResult?: Result<DialogportenStatementsResponse>;
  dialogportenOpenDialogId?: string;
  dialogportenDialogResult?: Result<DialogportenDialog>;

  prefillOrgNoInput: StringFieldModel = new StringFieldModel();
  prefillYearInput: StringFieldModel = new StringFieldModel();
  prefillResult?: Result<PrefillResponse>;
  prefillOpenDocumentKey?: string;

  constructor(http: AxiosInstance, history: History, messageStore: AlertMessageStore) {
    this.http = http;
    this.history = history;
    this.messageStore = messageStore;
    this.subscriptionForm = new CreateSubscriptionForm();
    this.inviteAdministratorForm = new InviteAdministratorForm();
    this.addCompanyForm = new AddCompanyForm();
    this.addUserForm = new AddUserForm();
    this.updateSubscriptionForm = new UpdateSubscriptionForm();
    this.searchTypeSelection = new RadioFieldModel<SearchType>({
      value: SearchType.dcompany,
      options: [SearchType.dcompany, SearchType.trank],
      idFn: (option) => option,
      labelFn: (option) => ({ key: option }),
      onChange: (option) => {
        if (option === SearchType.trank) this.clearCompany();
      },
    });
    this.trankSearchInput = new StringFieldModel({
      validator: organisationNumberValidator,
      required: true,
    });
    this.socialSecurityNumber = new StringFieldModel({ required: true });
    makeAutoObservable(this, { http: false, history: false }, { autoBind: true });
  }

  *init() {
    try {
      yield this.http.get("/superadmin");
    } catch (err) {
      this.history.replace("/");
      return;
    }

    yield this.fetchProducts();
  }

  get products() {
    return [
      ...(this.listProductResponse?.products ?? []),
      ...(this.listProductResponse?.inactiveProducts ?? []),
    ];
  }

  *fetchProducts() {
    try {
      this.listProductResponse = yield this.http.get(`payment/products`).then((r) => r.data);
      this.subscriptionForm.setProducts(this.listProductResponse!.products);
    } catch (e) {
      // no action
    }
  }

  *fetchEnterpriseAccountStats() {
    this.enterpriseAccountsResult = pending();
    try {
      const accounts: EnterpriseAccountStats[] = yield this.http
        .get(`enterprise-accounts`)
        .then((r) => r.data);
      this.enterpriseAccountsResult = ok(accounts);
    } catch (e) {
      this.enterpriseAccountsResult = failure();
    }
  }

  *updateDemoCompany() {
    if (!this.isDemoCompany) return;

    try {
      this.toggleDemoCompany = pending();
      yield this.http.post(`superadmin/update-demo-company`, {
        organisationNumber: this.companySearchResponse?.orgNo,
        isDemo: this.isDemoCompany.value,
      });
      this.toggleDemoCompany = ok();
      this.infoMessage = this.isDemoCompany.value
        ? `${this.companySearchResponse?.companyName} is now a demo company!`
        : `${this.companySearchResponse?.companyName} is _not_ a demo company!`;
    } catch (e) {
      this.toggleDemoCompany = failure();
      this.infoMessage = undefined;
      this.error = "Error when toggling demo company" + e;
    }
  }

  *submitTrankSearch() {
    this.trankSearchInput.validate();
    if (!this.trankSearchInput.isValid) {
      return;
    }
    const orgNo = this.trankSearchInput.inputValue;
    yield this.lookupCompany(orgNo);
    if (this.companySearchResponse && !this.error) {
      this.history.replace(`${superadminUrl}/search/${orgNo}`);
    }
  }

  loginAsAdmin(orgNo: string) {
    this.impersonationDialog = new ImpersonationDialogModel({
      onSubmit: (data) =>
        this.http
          .post<GenerateAdminLinkResponse>(
            `/superadmin/impersonate-company-administrator/${orgNo}`,
            data,
          )
          .then((response) => {
            if (response.data.isCustomer) {
              window.location.href = response.data.link;
            } else {
              alert("Not a customer");
              runInAction(() => this.setResultAdminLinkResponse(response.data));
            }
          })
          .catch((err) => {
            runInAction(() => {
              this.error = err.toString();
            });
          })
          .finally(() => {
            this.impersonationDialog = undefined;
          }),

      onCancel: () => {
        this.impersonationDialog = undefined;
      },
    });
  }

  impersonateUser(id: string) {
    this.impersonationDialog = new ImpersonationDialogModel({
      onSubmit: action((data) =>
        this.http.post(`/superadmin/login-as-impersonated-user/${id}`, data).finally(() => {
          this.impersonationDialog = undefined;
          window.location.href = "/";
        }),
      ),

      onCancel: action(() => {
        this.impersonationDialog = undefined;
      }),
    });
  }

  *lookupCompany(orgNo: string) {
    this.pageState = "initial";
    try {
      const response: CompanySearchResponse = yield this.http
        .get(`/superadmin/company/${orgNo}`)
        .then((r) => r.data);
      this.setResultLookupResponse(response);
      this.setAffiliates(response.affiliates);
      this.error = undefined;

      this.isDemoCompany = new RadioFieldModel<boolean>({
        value: this.companySearchResponse?.isDemo ?? false,
        options: [true, false],
        idFn: (v) => String(v),
        labelFn: (v) => ({ key: v ? "yes" : "no" }),
        onChange: () => this.updateDemoCompany(),
      });
    } catch (e) {
      this.error = `${e}`;
    }
  }

  clearCompany() {
    this.companySearchResponse = undefined;
    this.infoMessage = undefined;
  }

  *grantAdminAccess(externalId: string, organisationNumbers: string) {
    this.grantAdminAccessResult = pending();
    const orgNos = organisationNumbers.split(/\s+|,/).filter((s) => s.trim().length > 0);

    try {
      const response: GrantAdminAccessResponse = yield this.http
        .post<void>(`/superadmin/grant-admin-access`, {
          externalId: externalId,
          orgNos: orgNos,
        })
        .then((r) => r.data);
      this.grantAdminAccessResult = ok(response);
    } catch (err) {
      this.grantAdminAccessResult = failure();
      this.messageStore.showGeneralError();
      return;
    }
    const access: Result<UserAccess> = yield this.fetchUserAccess(externalId);
    if (
      isOk(this.userDetailsResult) &&
      this.userDetailsResult.data.user.externalId === externalId
    ) {
      this.userDetailsResult = ok({ ...this.userDetailsResult.data, access });
    }
  }

  fetchUserAccess(externalId: string): Promise<Result<UserAccess>> {
    return this.http
      .get<UserAccess>(`/superadmin/user/${externalId}/access`)
      .then((r) => ok(r.data))
      .catch(() => failure());
  }

  onboardLookedupCompany() {
    this.pageState = "isOnboarding";
    this.http
      .get<void>(`/superadmin/onboard-company/${this.companySearchResponse?.orgNo}`)
      .then(() => {
        runInAction(() => {
          this.pageState = "onboardingSuccess";
          this.lookupCompany(this.companySearchResponse!.orgNo);
        });
      })
      .catch((err) => {
        runInAction(() => {
          this.pageState = "initial";
          this.error = err.toString();
        });
      });
  }

  unacceptCompanyTerms() {
    this.http
      .get<void>(`/superadmin/unaccept-companyterms/${this.companySearchResponse?.orgNo}`)
      .then(() => {
        runInAction(() => {
          this.lookupCompany(this.companySearchResponse!.orgNo);
        });
      })
      .catch((err) => {
        runInAction(() => {
          this.pageState = "initial";
          this.error = err.toString();
        });
      });
  }

  *deleteAutoImportTransaction() {
    try {
      this.deleteAutoImportTransactionResult = pending();
      yield this.http.delete<void>(`superadmin/autoimport/${this.companySearchResponse?.orgNo}`);
      this.deleteAutoImportTransactionResult = ok();
    } catch (e) {
      const error = e as AxiosError;
      const result = error.response?.data as ValidationErrorWithContext[];
      this.deleteAutoImportTransactionResult = failure(
        join(
          result.map((it) => it.context),
          ", ",
        ),
      );
    }
  }
  *fetchZipImportFolders() {
    try {
      this.zipImportFolders = pending();
      const response: DestinationFoldersResponse = yield this.http
        .get<DestinationFoldersResponse>(
          `superadmin/archive-folders/${this.companySearchResponse?.orgNo}`,
        )
        .then((r) => r.data);
      this.zipImportFolders = ok(response.entries);
      this.zipImportFolder = defaultZipImportFolder(response.entries);
    } catch (e) {
      this.zipImportFolders = failure();
    }
  }

  selectZipImportFolder(folder: DestinationFolder) {
    this.zipImportFolder = folder;
  }

  selectZipImportFile(file: File) {
    this.zipImportFile = file;
    this.zipImportResult = undefined;
  }

  resetZipImport() {
    this.zipImportFolders = undefined;
    this.zipImportFolder = undefined;
    this.zipImportFile = undefined;
    this.zipImportResult = undefined;
  }

  *importZip() {
    if (!this.companySearchResponse || !this.zipImportFolder || !this.zipImportFile) return;
    try {
      const formData = new FormData();
      formData.append("file", this.zipImportFile);
      formData.append("orgNo", this.companySearchResponse.orgNo);
      formData.append("folderId", this.zipImportFolder.id);
      this.zipImportResult = pending();
      const response: ZipImportResult = yield this.http
        .post<ZipImportResult>(`superadmin/populate-archive`, formData)
        .then((r) => r.data);
      this.zipImportResult = ok(response);
      this.zipImportFile = undefined;
    } catch (e) {
      this.zipImportResult = failure(
        zipImportErrorMessage(isBadRequestError(e) ? e.response?.data : undefined),
      );
    }
  }

  clearResults() {
    this.fetchWhitelabelsResult = undefined;
    this.uploadWhitelabelLogoResult = undefined;
    this.fetchWhitelabelLogoResult = undefined;
  }

  *fetchWhitelabels() {
    try {
      this.fetchWhitelabelsResult = pending();
      const response: Whitelabel[] = yield this.http
        .get<Whitelabel[]>(`superadmin/whitelabels`)
        .then((r) => r.data);
      this.fetchWhitelabelsResult = ok(response);
    } catch (e) {
      this.fetchWhitelabelsResult = failure({ key: "general-error" });
    }
  }

  *fetchWhitelabelLogo(logoId: string) {
    try {
      this.fetchWhitelabelLogoResult = pending();
      const response: Blob = yield this.http.get(`whitelabel/logo/${logoId}`).then((r) => r.data);
      this.fetchWhitelabelLogoResult = ok(response);
    } catch (e) {
      this.fetchWhitelabelsResult = failure({ key: "general-error" });
    }
  }

  *uploadWhitelabelLogo(file: File) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      this.uploadWhitelabelLogoResult = pending();
      const response: string = yield this.http
        .post<string>(`superadmin/upload-whitelabel-logo`, formData)
        .then((r) => r.data);
      this.uploadWhitelabelLogoResult = ok(response);
    } catch (e) {
      this.uploadWhitelabelLogoResult = failure();
    }
  }

  *saveNewWhitelabel(whitelabel: Whitelabel, onSuccess: () => void) {
    try {
      this.fetchWhitelabelsResult = pending();
      const response: Whitelabel[] = yield this.http
        .post<Whitelabel[]>(`superadmin/save-new-whitelabel`, { ...whitelabel, id: newUuid() })
        .then((r) => r.data);
      this.fetchWhitelabelsResult = ok(response);
      onSuccess();
    } catch (e) {
      this.fetchWhitelabelsResult = failure({ key: "general-error" });
    }
  }

  *updateWhitelabel(whitelabel: Whitelabel, onSuccess: () => void) {
    try {
      this.fetchWhitelabelsResult = pending();
      const response: Whitelabel[] = yield this.http
        .post<Whitelabel[]>(`superadmin/update-whitelabel`, whitelabel)
        .then((r) => r.data);
      this.fetchWhitelabelsResult = ok(response);
      onSuccess();
    } catch (e) {
      this.fetchWhitelabelsResult = failure({ key: "general-error" });
    }
  }

  validateAndSubmitSubscriptionForm() {
    this.subscriptionForm.validate();
    if (this.subscriptionForm.isValid) {
      this.createSubscription();
    }
  }

  validateAndSubmitInviteAdministratorForm() {
    this.inviteAdministratorForm.validate();
    if (this.inviteAdministratorForm.validationErrors.length === 0) {
      this.inviteNewAdministrator();
    } else {
      logDebug("validation errors:", this.inviteAdministratorForm.validationErrors);
    }
  }

  createSubscription() {
    this.http
      .post<void>(`/superadmin/subscription`, {
        ownerOrganisationNumber: this.companySearchResponse?.orgNo,
        productId: this.subscriptionForm.productId,
        priceId: isNotBlank(this.subscriptionForm.priceId)
          ? this.subscriptionForm.priceId
          : undefined,
        expiresAt: this.subscriptionForm.expiresAt.validatedValue,
      })
      .then(() => {
        runInAction(() => {
          this.lookupCompany(this.companySearchResponse!.orgNo);
        });
      })
      .catch((err) => {
        runInAction(() => {
          this.pageState = "initial";
          this.error = err.toString();
        });
      });
  }

  inviteNewAdministrator() {
    this.http
      .post<void>(`/superadmin/invite-user/${this.companySearchResponse?.orgNo}`, {
        recipients: [{ email: this.inviteAdministratorForm.email }],
        language: this.inviteAdministratorForm.language,
      })
      .then(() => {
        runInAction(() => {
          this.lookupCompany(this.companySearchResponse!.orgNo);
          this.infoMessage = `Invitation is sent to ${this.inviteAdministratorForm.email}`;
        });
      })
      .catch((err) => {
        runInAction(() => {
          this.error = err.toString();
        });
      });
  }

  *replayTransactions() {
    this.replayResult = pending();
    try {
      yield this.http.post(`superadmin/replay-transactions/${this.companySearchResponse?.orgNo}`);
      this.replayResult = ok({ translated: "Replay OK" });
    } catch (e) {
      this.replayResult = failure({ translated: "Replay failed" });
    }
  }

  *exportToTRank() {
    try {
      yield this.http.get(`superadmin/trigger-trank-export/${this.companySearchResponse?.orgNo}`);
      this.infoMessage = `triggered export to TRank!`;
    } catch (err) {
      this.infoMessage = undefined;
      this.error = `Error when replaying transactions: ${err}`;
    }
  }

  *deleteCompany() {
    try {
      yield this.http.post(`superadmin/delete-company/${this.companySearchResponse?.orgNo}`);
      runInAction(() => {
        this.infoMessage = `Company has been deleted!`;
        this.lookupCompany(this.companySearchResponse!.orgNo);
      });
    } catch (err) {
      this.infoMessage = undefined;
      this.error = `Error when deleting company: ${err}`;
    }
  }

  *reimportRoles() {
    try {
      yield this.http.post(`superadmin/reimport-roles/${this.companySearchResponse?.orgNo}`);
      this.messageStore.showSuccess({
        translated: `Reimported roles for ${this.companySearchResponse?.companyName}`,
      });
    } catch (err) {
      if (isBadRequestError(err)) {
        this.messageStore.showError({
          translated: `Error when deleting company: ${err.response?.data}`,
        });
      } else {
        this.messageStore.showGeneralError();
      }
    }
  }

  isOnboarding() {
    return this.pageState === "isOnboarding";
  }

  isOnboardingSuccess() {
    return this.pageState === "onboardingSuccess";
  }

  setResultAdminLinkResponse(result: GenerateAdminLinkResponse) {
    this.adminLinkResponse = result;
  }

  setResultLookupResponse(result: CompanySearchResponse) {
    this.companySearchResponse = result;
    this.updateSubscriptionForm.subscriptionEndDate.setInputValue(
      result.subscription?.subscriptionEndDate?.substring(0, 10),
    );
  }

  private setAffiliates(affiliates: AffiliateItem[]) {
    if (affiliates.length > 0) {
      const active = affiliates.filter((it) => it.active);
      const current: AffiliateItem | undefined = active.length >= 1 ? active[0] : undefined;
      const noSelectionFallback: AffiliateItem = {
        id: "",
        key: "Not selected",
        active: !current,
      };
      this.affiliatesModel = new RadioFieldModel<AffiliateItem>({
        value: current ?? noSelectionFallback,
        options: [noSelectionFallback].concat(affiliates),
        idFn: (item) => item.id,
        labelFn: (item) => ({ translated: item.key }),
        onChange: (item) => {
          if (this.affiliatesModel) {
            this.affiliatesModel.value = item;
          }
        },
      });
    }
  }

  onboardCompany() {
    if (this.addCompanyForm.isValid()) {
      this.http
        .post(`superadmin/company/new`, [
          {
            organisationNumber: {
              organisationNumber: this.addCompanyForm.organisationNumber.inputValue,
              country: this.addCompanyForm.country.value.code,
            },
            name: this.addCompanyForm.name.inputValue,
          },
        ])
        .then((r) => {
          runInAction(() => {
            this.infoMessage = `${r.data}`;
          });
        })
        .catch(() => {
          runInAction(() => {
            this.infoMessage = undefined;
            this.error = `Error when onboarding ${this.addCompanyForm.name.inputValue} (${this.addCompanyForm.country.value.code}${this.addCompanyForm.organisationNumber.inputValue})`;
          });
        });
    }
  }

  *updateSubscriptionEndDate(subscriptionEndDate: string | undefined) {
    try {
      yield this.http.post(`superadmin/update-subscription-endtime`, {
        subscriptionId: this.companySearchResponse?.subscription?.subscriptionId,
        subscriptionEndTime: subscriptionEndDate,
      });
      if (subscriptionEndDate) {
        this.infoMessage = `Changed subscription end date to ${subscriptionEndDate}`;
      } else {
        this.infoMessage = `Removed subscription end date`;
      }
      if (this.companySearchResponse?.orgNo) {
        this.lookupCompany(this.companySearchResponse?.orgNo);
      }
    } catch (err) {
      this.infoMessage = undefined;
      this.error = "Error when updating subscription end date" + err;
    }
  }

  updateAffiliate(item: AffiliateItem) {
    this.http
      .post(`superadmin/update-affiliate`, {
        organisationNumber: this.companySearchResponse?.orgNo,
        affiliateId: isEmpty(item.id) ? null : item.id,
      })
      .then(() => {
        runInAction(() => {
          this.infoMessage = `Changed affiliate to ${item.key}`;
          if (this.companySearchResponse?.orgNo) {
            this.lookupCompany(this.companySearchResponse?.orgNo);
          }
        });
      })
      .catch((error) => {
        runInAction(() => {
          this.infoMessage = undefined;
          this.error = "Error when updating affiliate" + error.toString();
        });
      });
  }

  updateSubscriptionType(product: ProductDescription) {
    this.http
      .post(`superadmin/update-subscription-type`, {
        subscriptionId: this.companySearchResponse?.subscription?.subscriptionId,
        productId: product.productId,
      })
      .then(() => {
        runInAction(() => {
          this.infoMessage = `Changed subscription type to ${product.productName}`;
          if (this.companySearchResponse?.orgNo) {
            this.lookupCompany(this.companySearchResponse?.orgNo);
          }
        });
      })
      .catch((err) => {
        runInAction(() => {
          this.infoMessage = undefined;
          this.error = "Error when updating subscription type" + err.toString();
        });
      });
  }

  get subscription() {
    return this.availableProducts.find(
      (i) => i.productId === this.companySearchResponse?.subscription?.productId,
    );
  }

  get availableProducts() {
    return [
      ...(this.listProductResponse?.products ?? []),
      ...(this.listProductResponse?.inactiveProducts ?? []),
    ];
  }

  saveSubscriptionEndDate() {
    this.updateSubscriptionForm.subscriptionEndDate.validate();
    if (this.updateSubscriptionForm.subscriptionEndDate.isValid) {
      this.updateSubscriptionEndDate(
        this.updateSubscriptionForm.subscriptionEndDate.validatedValue,
      );
    }
  }

  createUser() {
    if (this.addUserForm.isValid()) {
      this.http
        .post(`superadmin/user/new`, {
          firstName: this.addUserForm.firstName.validatedValue,
          middleName: this.addUserForm.middleName.validatedValue,
          lastName: this.addUserForm.lastName.validatedValue,
          email: this.addUserForm.email.validatedValue,
          nationalId: this.addUserForm.nationalId.validatedValue,
          country: this.addUserForm.country.value.code,
        })
        .then((r) => {
          runInAction(() => {
            this.infoMessage = `Successfully added user with id ${r.data.userId}, email ${r.data.email} and password ${r.data.password}`;
            this.addUserForm.clearForm();
          });
        })
        .catch((error) => {
          if (error.response?.status === 400) {
            logDebug("Validation error when adding new user", error);
            runInAction(() => {
              this.infoMessage = undefined;
              this.error = `${error.response?.data}.`;
            });
          } else {
            runInAction(() => {
              this.infoMessage = undefined;
              this.error = "Error when adding new admin user";
            });
          }
        });
    }
  }

  *search() {
    if (this.socialSecurityNumber.isValid) {
      this.searchedUserState = { type: "loading" };
      const users: UserByEmailOrSocialSecurityNumber[] = yield this.http
        .get<UserByEmailOrSocialSecurityNumber[]>(
          `/superadmin/lookup-user/${this.socialSecurityNumber.validatedValue}`,
        )
        .then((r) => r.data)
        .catch((err) => {
          this.error = err.toString();
        });
      this.searchedUserState = { type: "result", users };
    }
  }

  *loadUserDetails(userId: string) {
    this.userDetailsResult = pending();
    this.grantAdminAccessResult = undefined;
    try {
      const users: UserByEmailOrSocialSecurityNumber[] = yield this.http
        .get<UserByEmailOrSocialSecurityNumber[]>(`/superadmin/lookup-user/${userId}`)
        .then((r) => r.data);
      const user = users.find((u) => String(u.userId) === userId || u.externalId === userId);
      if (!user) {
        this.userDetailsResult = failure(`No user with id ${userId}`);
        return;
      }
      const access: Result<UserAccess> = yield this.fetchUserAccess(user.externalId);
      this.userDetailsResult = ok({ user, access });
    } catch (e) {
      logDebug("Could not load user details", e);
      this.userDetailsResult = failure(`Could not load user ${userId}`);
    }
  }

  *unlockUser(externalId: string) {
    this.unlockUserResult = pending();
    try {
      const user: UserByEmailOrSocialSecurityNumber = yield this.http
        .post<UserByEmailOrSocialSecurityNumber>(`/superadmin/user/${externalId}/unlock`)
        .then((r) => r.data);
      this.unlockUserResult = ok(undefined);
      if (
        isOk(this.userDetailsResult) &&
        this.userDetailsResult.data.user.externalId === externalId
      ) {
        this.userDetailsResult = ok({ ...this.userDetailsResult.data, user });
      }
      if (this.searchedUserState.type === "result") {
        this.searchedUserState = {
          ...this.searchedUserState,
          users: this.searchedUserState.users?.map((u) => (u.externalId === externalId ? user : u)),
        };
      }
    } catch (e) {
      logDebug("Could not unlock user", e);
      this.unlockUserResult = failure();
      this.messageStore.showGeneralError();
    }
  }

  initAnnouncementState() {
    this.http
      .get<GetAnnouncementResponse>("/superadmin/announcement")
      .then((res) =>
        this.setAnnouncementState(
          res.data.announcement
            ? { type: "active", announcement: res.data.announcement }
            : { type: "empty" },
        ),
      )
      .catch(() => {
        this.messageStore.showGeneralError();
        this.setAnnouncementState({ type: "empty" });
      });
  }

  addAnnouncement() {
    this.setAnnouncementState({
      type: "input",
      level: new RadioFieldModel<AnnouncementDTO["level"]>({
        value: "info",
        idFn: identity,
        labelFn: (level) => ({ translated: capitalize(level) }),
        options: ["info", "warning", "error", "success"],
      }),
      message: Object.values(Language).map(
        (l) =>
          new LocalizedStringFieldModel(new StringFieldModel({ required: true }), l as Language),
      ),
    });
  }

  cancelAddAnnouncement() {
    this.setAnnouncementState({ type: "empty" });
  }

  saveAnnouncement(state: Extract<AnnouncementState, { type: "input" }>) {
    state.message.forEach((m) => m.model.validate());
    if (state.message.some((m) => !m.model.isValid)) {
      return;
    }
    this.setAnnouncementState({ ...state, saving: true });
    this.http
      .put<GetAnnouncementResponse>("/superadmin/announcement", {
        announcementId: newUuid(),
        level: state.level.value,
        message: state.message.map((m) => ({ language: m.language, text: m.text })),
      } satisfies AnnouncementDTO)
      .then((res) =>
        this.setAnnouncementState(
          res.data.announcement
            ? { type: "active", announcement: res.data.announcement }
            : { type: "empty" },
        ),
      )
      .catch(() => {
        this.messageStore.showGeneralError();
        this.setAnnouncementState({ ...state, saving: false });
      });
  }

  removeAnnouncement(state: Extract<AnnouncementState, { type: "active" }>) {
    this.setAnnouncementState({ ...state, deleting: true });
    this.http
      .delete("/superadmin/announcement")
      .then(() => this.setAnnouncementState({ type: "empty" }));
  }

  setAnnouncementState(state: AnnouncementState) {
    this.announcementState = state;
  }

  get enterpriseAccountsStats() {
    return isOk(this.enterpriseAccountsResult)
      ? this.filterEnterpriseAccountsData(this.enterpriseAccountsResult.data)
      : [];
  }

  private filterEnterpriseAccountsData(data: EnterpriseAccountStats[]) {
    const nameFilter = this.enterpriseAccountsNameFilter.inputValue.trim().toLowerCase();
    const filteredStats = nameFilter
      ? data.filter((d) => d.account.name.toLowerCase().includes(nameFilter))
      : data;
    const statusFilter = this.enterpriseAccountsStatusFilter.value;
    return statusFilter
      ? filteredStats.filter((s) => s.account.status === statusFilter)
      : filteredStats;
  }

  *setEnterpriseAccount(orgNo: string, enterpriseAccountId: string) {
    try {
      yield this.http.post(`/superadmin/company/${orgNo}`, { enterpriseAccountId });
      yield this.lookupCompany(orgNo);
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  *fetchCustomers() {
    this.customerListResult = pending();
    try {
      const customers: CustomerListing[] = yield this.http
        .get(`superadmin/customers`)
        .then((r) => r.data);
      this.customerListResult = ok(customers);
    } catch (e) {
      this.customerListResult = failure();
    }
  }

  setCustomerSubscriptionFilter(value: string | null) {
    this.customerSubscriptionFilter = value;
  }

  get customerSubscriptionOptions(): string[] {
    if (!isOk(this.customerListResult)) return [];
    const names = new Set<string>();
    for (const c of this.customerListResult.data) {
      if (c.subscriptionName) names.add(c.subscriptionName);
    }
    return [...names].sort();
  }

  get filteredCustomers(): CustomerListing[] {
    if (!isOk(this.customerListResult)) return [];
    const subscriptionFilter = this.customerSubscriptionFilter;
    return this.customerListResult.data.filter((c) => {
      if (subscriptionFilter === null) return true;
      if (subscriptionFilter === CUSTOMER_FILTER_NO_SUBSCRIPTION) return !c.subscriptionName;
      return c.subscriptionName === subscriptionFilter;
    });
  }

  downloadCustomersCsv() {
    window.location.href = "/api/superadmin/customers?format=csv";
  }

  get dialogportenOrgNo(): string {
    return this.dialogportenOrgNoInput.inputValue.trim();
  }

  setDialogportenIncludeAllResources(value: boolean) {
    this.dialogportenIncludeAllResources = value;
  }

  *fetchDialogportenStatements() {
    const orgNo = this.dialogportenOrgNo;
    if (!orgNo) return;
    this.dialogportenResult = pending();
    this.dialogportenOpenDialogId = undefined;
    this.dialogportenDialogResult = undefined;
    const query = this.dialogportenIncludeAllResources ? "?includeAllResources=true" : "";
    try {
      const response: DialogportenStatementsResponse = yield this.http
        .get(`superadmin/ar/dialogporten/${orgNo}${query}`)
        .then((r) => r.data);
      this.dialogportenResult = ok(response);
    } catch (e) {
      this.dialogportenResult = failure();
    }
  }

  *toggleDialogportenDialog(dialogId: string) {
    if (this.dialogportenOpenDialogId === dialogId) {
      this.dialogportenOpenDialogId = undefined;
      this.dialogportenDialogResult = undefined;
      return;
    }
    this.dialogportenOpenDialogId = dialogId;
    this.dialogportenDialogResult = pending();
    try {
      const dialog: DialogportenDialog = yield this.http
        .get(`superadmin/ar/dialogporten/${this.dialogportenOrgNo}/${dialogId}`)
        .then((r) => r.data);
      this.dialogportenDialogResult = ok(dialog);
    } catch (e) {
      this.dialogportenDialogResult = failure();
    }
  }

  async downloadDialogportenAttachment(
    dialogId: string,
    transmissionId: string,
    attachmentId: string,
    reportingYear: number,
  ) {
    const url =
      `superadmin/ar/dialogporten/${this.dialogportenOrgNo}/${dialogId}` +
      `/${transmissionId}/${attachmentId}?year=${reportingYear}`;
    try {
      const response = await this.http.get(url, { responseType: "blob" });
      downloadFile(response.data, getFilenameFromHeaders(response.headers));
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  get prefillOrgNo(): string {
    return this.prefillOrgNoInput.inputValue.trim();
  }

  get prefillYear(): string {
    return this.prefillYearInput.inputValue.trim();
  }

  toggleOpenPrefillDocument(key: string) {
    this.prefillOpenDocumentKey = this.prefillOpenDocumentKey === key ? undefined : key;
  }

  *fetchPrefill() {
    const orgNo = this.prefillOrgNo;
    const year = this.prefillYear;
    if (!orgNo || !year) return;
    this.prefillResult = pending();
    this.prefillOpenDocumentKey = undefined;
    try {
      const response: PrefillResponse = yield this.http
        .get(`superadmin/ar/prefill/${orgNo}/${year}`)
        .then((r) => r.data);
      this.prefillResult = ok(response);
    } catch (e) {
      this.prefillResult = failure();
    }
  }

  *refreshPrefill() {
    const orgNo = this.prefillOrgNo;
    const year = this.prefillYear;
    if (!orgNo || !year) return;
    this.prefillResult = pending();
    this.prefillOpenDocumentKey = undefined;
    try {
      const response: PrefillResponse = yield this.http
        .post(`superadmin/ar/prefill/${orgNo}/${year}/refresh`)
        .then((r) => r.data);
      this.prefillResult = ok(response);
    } catch (e) {
      this.prefillResult = failure();
    }
  }
}
